// Which database this site uses (backup plan, the database-only switch; Umer, 2026-10-23).
//
// Normally the main (VITE_SUPABASE_URL). After a DATABASE-ONLY switch the backup database is in charge while staff
// stay on this site: the browser then talks to the backup (VITE_BACKUP_SUPABASE_URL) — no redeploy. The backup
// database says so itself: public.wolf_site_state() = { state: 'in_charge', scope: 'database' } (0138, 0141). After a
// FULL switch this site stays on the main, which is paused (staff work on wolfdrivers.net).
//
// The choice is made once per page load from the last decision this browser saw (localStorage), so the client is
// created at once; a watcher asks the backup every 30 s while the page is shown (and on coming back to it) and
// reloads the page once when the answer changed. The sign-in is kept under ONE storage key whichever database is
// used, so nobody is logged out by a switch; a token the other database issued is refused once (401) and renewed
// there by the client's own refresh (services/supabaseClient makeAuthAwareFetch) — the refresh tokens are copied
// both ways (copy-logins.sh, copy-home.mjs).
//
// On only with the backup switch on (VITE_BACKUP_SYNC) and a backup address that differs from this site's own —
// the backup site (wolfdrivers.net) is always on the backup database and never switches.
import { FEATURES } from './features';

const trim = (u?: string) => String(u || '').replace(/\/+$/, '');
const CHOICE_KEY = 'wolf:liveDatabase';
const RELOADED_KEY = 'wolf:liveDatabaseReloadedAt';
const CHECK_MS = 30_000;
const TIMEOUT_MS = 4_000;

export type DatabaseSide = 'main' | 'backup';

export const mainDatabaseUrl = (): string => trim(import.meta.env.VITE_SUPABASE_URL);
export const backupDatabaseUrl = (): string => trim(import.meta.env.VITE_BACKUP_SUPABASE_URL);

export function liveDatabaseEnabled(): boolean {
  const backup = backupDatabaseUrl();
  return !!FEATURES.backupSync && !!backup && !!mainDatabaseUrl() && backup !== mainDatabaseUrl();
}

function stored(): DatabaseSide {
  try { return localStorage.getItem(CHOICE_KEY) === 'backup' ? 'backup' : 'main'; } catch { return 'main'; }
}
function store(side: DatabaseSide): void {
  try { localStorage.setItem(CHOICE_KEY, side); } catch { /* private window: the next check decides again */ }
}

/** The side this page load uses (decided once, at the client's creation). */
let current: DatabaseSide | null = null;
export function currentDatabaseSide(): DatabaseSide {
  if (current === null) current = liveDatabaseEnabled() ? stored() : 'main';
  return current;
}

/** The address the client is created with. */
export function liveDatabaseUrl(): string {
  return currentDatabaseSide() === 'backup' ? backupDatabaseUrl() : mainDatabaseUrl();
}

/** One storage key for the sign-in whichever database is used: supabase-js's default for the MAIN address, so
 *  sessions saved before this existed are found too. undefined = the library's own default (switch off). */
export function sessionStorageKey(): string | undefined {
  if (!liveDatabaseEnabled()) return undefined;
  try { return `sb-${new URL(mainDatabaseUrl()).hostname.split('.')[0]}-auth-token`; } catch { return undefined; }
}

/** What the backup database says right now: 'backup' when it took over the database only, else 'main';
 *  null = no answer (keep what we have). */
export async function askBackupDatabase(fetcher: typeof fetch = fetch): Promise<DatabaseSide | null> {
  if (!liveDatabaseEnabled()) return 'main';
  const key = String(import.meta.env.VITE_SUPABASE_ANON_KEY || '');
  try {
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), TIMEOUT_MS) : null;
    const res = await fetcher(`${backupDatabaseUrl()}/rest/v1/rpc/wolf_site_state`, {
      method: 'POST',
      headers: { apikey: key, authorization: `Bearer ${key}`, 'content-type': 'application/json' },
      body: '{}',
      ...(ctl ? { signal: ctl.signal } : {}),
    });
    if (timer) clearTimeout(timer);
    if (!res.ok) return null;
    const site = await res.json();
    return site && site.state === 'in_charge' && site.scope === 'database' ? 'backup' : 'main';
  } catch {
    return null;
  }
}

/** Ask now; when the answer differs from this page's database, remember it and reload once (never twice within
 *  20 s — a flapping answer cannot loop the page). Returns the answer. */
export async function checkLiveDatabase(opts: { fetcher?: typeof fetch; reload?: () => void; now?: number } = {}): Promise<DatabaseSide | null> {
  const answer = await askBackupDatabase(opts.fetcher);
  if (answer === null || answer === currentDatabaseSide()) return answer;
  store(answer);
  const now = opts.now ?? Date.now();
  let last = 0;
  try { last = Number(sessionStorage.getItem(RELOADED_KEY) || 0); } catch { /* ignore */ }
  if (now - last < 20_000) return answer;
  try { sessionStorage.setItem(RELOADED_KEY, String(now)); } catch { /* ignore */ }
  (opts.reload ?? (() => window.location.reload()))();
  return answer;
}

/** Watch while the app runs: at once, every 30 s while shown, and on coming back to the page. */
export function startLiveDatabaseWatch(): () => void {
  if (!liveDatabaseEnabled() || typeof window === 'undefined') return () => {};
  const tick = () => { if (document.visibilityState === 'visible') void checkLiveDatabase(); };
  void checkLiveDatabase();
  const timer = window.setInterval(tick, CHECK_MS);
  document.addEventListener('visibilitychange', tick);
  return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', tick); };
}

/** test only */
export function _resetLiveDatabase(): void { current = null; }
